import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    Pagination
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { getClinic } from "../http/clinicAPI";
import axios from "axios";

const STATUS_COLORS = {
    CREATED: "default",
    PENDING: "warning",
    SENT: "success",
    FAILED: "error",
    CANCELED: "error",
};

const CASCADE_STATUS_COLORS = {
    PENDING: "warning",
    COMPLETED: "success",
    FAILED: "error",
};

const CASCADE_STEP_LABELS = {
    telegram: "Telegram",
    sms_aero: "SMS Aero",
};

const CascadeMessageHistory = () => {
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState(null);

    const rowsPerPage = 20;

    useEffect(() => {
        fetchMessages();
    }, [page]);

    const fetchMessages = async () => {
        try {
            const clinic = await getClinic();
            const response = await axios.post("/web/cascade/messages", {
                params: {
                    page: page,
                    limit: rowsPerPage,
                    sortBy: "createdAt",
                    sortOrder: "DESC"
                }
            });

            setMessages(response.data.messages || []);
            setTotalPages(response.data.totalPages || 1);
            setError(null);
        } catch (err) {
            console.error("Ошибка загрузки сообщений:", err);
            setError("Не удалось загрузить историю сообщений");
        } finally {
            setLoading(false);
        }
    };

    const formatStatus = (status) => {
        const labels = {
            CREATED: "Создано",
            PENDING: "В очереди",
            SENT: "Отправлено",
            FAILED: "Ошибка",
            CANCELED: "Отменено"
        };
        return labels[status] || status;
    };

    const formatCascadeStatus = (status) => {
        const labels = {
            PENDING: "В прогрессе",
            COMPLETED: "Завершен",
            FAILED: "Ошибка"
        };
        return labels[status] || status;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleString("ru-RU", {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "400px"
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                История каскадных сообщений
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    Отображается история отправок сообщений с каскадной логикой: 
                    сначала отправка в Telegram, затем SMS Aero при неудаче.
                </Typography>
            </Alert>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {messages.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="textSecondary">
                        Нет сообщений в истории
                    </Typography>
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ mb: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Сообщение</TableCell>
                                    <TableCell>Канал</TableCell>
                                    <TableCell>Статус канала</TableCell>
                                    <TableCell>Статус каскада</TableCell>
                                    <TableCell>Получатель</TableCell>
                                    <TableCell>Ошибка</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {messages.map((message) => (
                                    <TableRow key={message.id}>
                                        <TableCell>
                                            {formatDate(message.statusUpdatedAt || message.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {message.message || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={CASCADE_STEP_LABELS[message.cascadeStep] || message.cascadeStep}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatStatus(message.status)}
                                                color={STATUS_COLORS[message.status]}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatCascadeStatus(message.cascadeStatus)}
                                                color={CASCADE_STATUS_COLORS[message.cascadeStatus]}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {message.user?.phone || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {message.errorMessage && (
                                                <Typography 
                                                    variant="body2" 
                                                    color="error"
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {message.errorMessage}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(event, newPage) => setPage(newPage)}
                                variant="outlined"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default CascadeMessageHistory;